import React, { useState } from 'react';
import { Upload, Lock, Loader2, Video } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { toast } from '../hooks/use-toast';
import api, { getToken, setToken } from '../lib/api';

export default function TeachingUploadDialog({ open, onOpenChange, onUploaded, defaultSeries }) {
  const [authed, setAuthed] = useState(!!getToken());
  const [passcode, setPasscode] = useState('');
  const [title, setTitle] = useState('');
  const [series, setSeries] = useState('');
  const [speaker, setSpeaker] = useState('Pastor Oladipo Babalola');
  const [duration, setDuration] = useState('');
  const [description, setDescription] = useState('');
  const [video, setVideo] = useState(null);
  const [audio, setAudio] = useState(null);
  const [thumb, setThumb] = useState(null);
  const [pdf, setPdf] = useState(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);

  React.useEffect(() => {
    if (open && defaultSeries) setSeries(defaultSeries);
  }, [open, defaultSeries]);

  const reset = () => {
    setTitle(''); setSeries(''); setDuration(''); setDescription('');
    setVideo(null); setAudio(null); setThumb(null); setPdf(null);
    setPasscode(''); setProgress(0);
  };

  const signIn = async (e) => {
    e.preventDefault();
    if (!passcode.trim()) return;
    try {
      const { token } = await api.login(passcode.trim());
      setToken(token); setAuthed(true);
      toast({ title: 'Signed in', description: 'You may now publish a teaching.' });
    } catch {
      toast({ title: 'Incorrect passcode' });
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!title) { toast({ title: 'Title is required' }); return; }
    if (!video && !audio) { toast({ title: 'Add a video (MP4) or audio (MP3)' }); return; }
    setBusy(true); setProgress(0);
    try {
      const fd = new FormData();
      fd.append('title', title);
      fd.append('series', series || 'Teaching');
      fd.append('speaker', speaker || 'Pastor Oladipo Babalola');
      fd.append('duration', duration || '');
      fd.append('description', description || '');
      if (video) fd.append('video', video);
      if (audio) fd.append('audio', audio);
      if (thumb) fd.append('thumbnail', thumb);
      if (pdf) fd.append('pdf', pdf);
      await api.createTeaching(fd, (evt) => {
        if (evt.total) setProgress(Math.round((evt.loaded / evt.total) * 100));
      });
      toast({ title: 'Teaching published', description: 'It is now live in the library.' });
      reset();
      onUploaded && onUploaded();
      onOpenChange(false);
    } catch (err) {
      toast({ title: 'Publish failed', description: String(err?.response?.data?.detail || err.message) });
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!busy) onOpenChange(v); }}>
      <DialogContent className="sm:max-w-lg bg-white max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-serif-display text-2xl flex items-center gap-2">
            <Video className="h-5 w-5 text-teal-deep" /> Publish a Teaching
          </DialogTitle>
        </DialogHeader>

        {!authed ? (
          <form onSubmit={signIn} className="space-y-4 mt-2">
            <div className="flex items-center gap-3 p-3 rounded-lg bg-cream/60">
              <Lock className="h-4 w-4 text-teal-deep" />
              <p className="text-sm text-ink/70">Publishing is protected. Enter the ministry passcode to continue.</p>
            </div>
            <div>
              <Label htmlFor="tpc">Passcode</Label>
              <Input id="tpc" type="password" value={passcode} onChange={e => setPasscode(e.target.value)} placeholder="Enter passcode" autoFocus />
            </div>
            <Button type="submit" className="w-full bg-teal-brand hover:bg-teal-deep text-white h-11">Continue</Button>
          </form>
        ) : (
          <form onSubmit={submit} className="space-y-4 mt-2">
            <div>
              <Label htmlFor="tt">Title *</Label>
              <Input id="tt" value={title} onChange={e => setTitle(e.target.value)} placeholder="Sermon title" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="ts">Series</Label>
                <Input id="ts" value={series} onChange={e => setSeries(e.target.value)} placeholder="Grace Series" />
              </div>
              <div>
                <Label htmlFor="td">Duration</Label>
                <Input id="td" value={duration} onChange={e => setDuration(e.target.value)} placeholder="38:22" />
              </div>
            </div>
            <div>
              <Label htmlFor="tsp">Speaker</Label>
              <Input id="tsp" value={speaker} onChange={e => setSpeaker(e.target.value)} />
            </div>
            <div>
              <Label htmlFor="tds">Description</Label>
              <Textarea id="tds" rows={3} value={description} onChange={e => setDescription(e.target.value)} />
            </div>
            <div>
              <Label htmlFor="tv">Video (MP4)</Label>
              <Input id="tv" type="file" accept="video/mp4,video/*" onChange={e => setVideo(e.target.files?.[0] || null)} />
              {video && <p className="text-xs text-ink/50 mt-1">{video.name}</p>}
            </div>
            <div>
              <Label htmlFor="ta">Audio (MP3)</Label>
              <Input id="ta" type="file" accept="audio/mpeg,audio/mp3,audio/*" onChange={e => setAudio(e.target.files?.[0] || null)} />
              {audio && <p className="text-xs text-ink/50 mt-1">{audio.name}</p>}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="tth">Thumbnail</Label>
                <Input id="tth" type="file" accept="image/*" onChange={e => setThumb(e.target.files?.[0] || null)} />
              </div>
              <div>
                <Label htmlFor="tpdf">Notes (PDF)</Label>
                <Input id="tpdf" type="file" accept="application/pdf" onChange={e => setPdf(e.target.files?.[0] || null)} />
              </div>
            </div>
            <p className="text-xs text-ink/50">You must provide at least a video or an audio file.</p>
            {busy && (
              <div className="p-3 rounded-lg bg-cream/60 border border-teal-brand/20">
                <div className="flex items-center gap-2 text-sm text-ink">
                  <Loader2 className="h-4 w-4 animate-spin text-teal-deep" /> Publishing… {progress}%
                </div>
                <div className="mt-2 h-1.5 rounded-full bg-white overflow-hidden">
                  <div className="h-full bg-gold-brand transition-all" style={{ width: `${progress}%` }} />
                </div>
              </div>
            )}
            <div className="flex gap-2">
              <Button type="submit" disabled={busy} className="flex-1 bg-gold-brand hover:bg-[#C48A1F] text-ink font-semibold h-11">
                {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />} Publish Teaching
              </Button>
              <Button type="button" variant="outline" onClick={() => { setToken(null); setAuthed(false); }} className="h-11" title="Sign out">
                <Lock className="h-4 w-4" />
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
